import { merge } from '../../editor/core/highcharts-editor';
import api from '../../utils/ajax';
import React from 'react';
import { saveChartAction, publishChartAction } from '../../pages/ChartEditorPage/actions/chartEditor';
import { saveLayoutAction, publishLayoutAction } from '../../pages/LayoutEditorPage/actions/layoutEditor';
import { saveTableAction, publishTableAction } from '../../pages/TableEditorPage/actions/tableEditor';
import { saveThemeAction } from 'pages/ThemeEditorPage/actions/themeEditor';
import { formatDistanceToNow } from 'date-fns';

export const colourStyles = {
  dropdownIndicator: (base, state) => ({
    ...base,
    color: '#28277E',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)'
  }),

  option: (provided, state) => ({
    ...provided,
    // eslint-disable-next-line no-nested-ternary
    color: state.isSelected ? '#6D5BD1' : state.isDisabled ? '#656565' : '#28277E',
    fontWeight: state.isSelected ? 700 : 500,
    boxShadow: null,
    borderRadius: 2,
    backgroundColor: state.isFocused && !state.isSelected ? '#AEC8FA' : '#fff',
    textTransform: 'capitalize'
  }),

  menu: (provided, state) => ({
    ...provided,
    fontWeight: 700,
    padding: 4,
    backgroundColor: '#F7F8F8',
    color: '#28277E',
    marginTop: '1px',
    borderRadius: state.selectProps.menuIsOpen ? '0px 0px 4px 4px' : '4px 4px 4px 4px',
    textAlign: 'left',
    textTransform: 'capitalize'
  }),

  control: (provided, state) => ({
    ...provided,
    border: 0,
    minHeight: '2.5rem',
    '&:hover': {
      backgroundColor: '#AEC8FA'
    },
    borderRadius: state.selectProps.menuIsOpen ? '4px 4px 0px 0px' : '4px 4px 4px 4px',
    backgroundColor: state.selectProps.menuIsOpen ? '#F7F8F8' : state.selectProps.backgroundColor ?? '#FFFFFF',
    color: '#28277E',
    fontWeight: 700,
    whiteSpace: 'nowrap'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#28277E',
    textTransform: 'capitalize'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),

  valueContainer: (provided) => ({
    ...provided,
    padding: '0px 8px',
    flexWrap: 'nowrap'
  })
};

export const editorStyles = {
  ...colourStyles,
  dropdownIndicator: (base, state) => ({
    ...base,
    position: 'absolute',
    right: '1rem',
    top: '0.75rem',
    height: '1rem',
    padding: 0,
    color: '#28277E',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    '&:hover': { color: '#28277E' },
    '& > svg': {
      width: '1.5rem',
      height: '1.5rem'
    }
  }),
  control: (provided, state) => ({
    ...provided,
    minHeight: '2.5rem',
    backgroundColor: state.selectProps.backgroundColor ?? '#FFFFFF',
    borderRadius: '0.25rem',
    paddingRight: '2.5rem',
    border: 'none',
    '&:hover': { backgroundColor: '#AEC8FA' }
  }),
  valueContainer: (provided) => ({
    ...provided,
    paddingLeft: '1rem',
    flexWrap: 'nowrap'
  })
};

export const richtextStyles = {
  ...editorStyles,
  valueContainer: (provided) => ({
    ...provided,
    flexWrap: 'nowrap'
  }),
  container: (provided) => ({
    ...provided,
    height: '2rem',
    width: '3.5rem'
  }),
  control: (provided) => ({
    ...provided,
    border: 0,
    height: '2rem',
    minHeight: '2rem',
    width: '3.5rem',
    '&:hover': {
      backgroundColor: '#AEC8FA'
    },
    color: '#28277E',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    flexWrap: 'nowrap'
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    padding: 0,
    color: '#28277E',
    position: 'absolute',
    right: '0.5rem',
    top: '0.5rem',
    width: '1rem',
    height: '1rem',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    '& > button': {
      width: '1rem !important',
      height: '1rem !important',
      padding: '0 !important'
    },
    '& svg': {
      margin: 'auto',
      float: 'unset !important'
    }
  })
};

export const fpsColourStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '30px',
    height: '36px',
    boxShadow: state.isFocused ? null : null
  }),

  valueContainer: (provided) => ({
    ...provided,
    height: '36px',
    padding: '0 6px'
  }),

  input: (provided) => ({
    ...provided,
    margin: '0px'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '36px'
  })
};

export const fpsOptions = [
  {
    value: 50,
    label: '50P (Pal)'
  },
  {
    value: '60i',
    label: '60i (NTSC)'
  },
  {
    value: 60,
    label: '60FPS (Web/Mobile)'
  }
];

export const imageOptions = [
  {
    key: 'image/png',
    label: 'PNG'
  },
  {
    key: 'image/jpeg',
    label: 'JPG'
  },
  {
    key: 'image/svg+xml',
    label: 'SVG'
  },
  {
    key: 'application/pdf',
    label: 'PDF'
  }
];

export const fileOptions = [
  {
    key: 'html',
    label: 'HTML'
  },
  {
    key: 'json',
    label: 'JSON'
  }
];

export const downloadImage = (key, chart) => {
  chart = chart ?? window.Everviz.evervizEditor.chart.options.all();
  chart.exportChart({
    type: key
  });
};

export const downloadTableImage = async (props, imageType = 'png') => {
  const { aggregatedOptions, type, cssModules, dataOptions } = props;
  const mimeTypes = {
    jpeg: 'image/jpeg',
    png: 'image/png',
    pdf: 'application/pdf'
  };

  let options = merge({}, aggregatedOptions);
  let aggOptions = {
    options,
    data: {
      value: dataOptions
    },
    cssModules: cssModules || []
  };

  const image = await fetch(api.getURL() + '/export', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      options: JSON.stringify(aggOptions),
      type: mimeTypes[imageType],
      constr: 'table',
      projectType: type,
      cssModules: JSON.stringify(cssModules || []),
      pluginConfig: JSON.stringify({})
    })
  });
  const imageBlog = await image.blob();
  const imageURL = URL.createObjectURL(imageBlog);

  const link = document.createElement('a');
  link.href = imageURL;
  link.download = `table.${imageType}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getPublishStatus = (published, saved, changeMade, lastPublishTime, lastSavedTime) => {
  const lastPublished = lastPublishTime && new Date(lastPublishTime);
  const lastSaved = lastSavedTime && new Date(lastSavedTime);

  if (lastPublished && lastSaved && lastSaved > lastPublished) {
    return {
      buttonText: 'Republish',
      maxHeight: 'max-h-[950px]',
      status: 'Published',
      text: 'Project is published with saved changes. Republish it to show saved changes in link and embed codes.'
    };
  }

  if (published && changeMade) {
    return {
      buttonText: 'Republish',
      maxHeight: 'max-h-[950px]',
      status: 'Published',
      text: 'Project is published, but has new changes. Republish it to show changes in link and embed codes.'
    };
  }
  if (published && !changeMade) {
    return {
      buttonText: 'Published',
      maxHeight: 'max-h-[950px]',
      status: 'Published',
      text: 'Project is published and saved. Project link and embed codes are public.',
      light: true
    };
  }
  if (saved) {
    return {
      buttonText: 'Publish',
      maxHeight: 'max-h-[275px]',
      status: 'Unpublished',
      text: 'Project is saved. Publish to generate a public link or embed code.'
    };
  }
  return {
    buttonText: 'Publish',
    maxHeight: 'max-h-[185px]',
    status: 'Unsaved',
    text: 'Save your progress so far or publish to generate a project link or embed code.'
  };
};

export const onPublish = (type, dispatch) => {
  const publishMap = {
    chart: publishChartAction,
    layout: publishLayoutAction,
    table: publishTableAction
  };

  // eslint-disable-next-line no-use-before-define
  onSave(type, false, dispatch, () => {
    dispatch(
      publishMap[type]({
        inspired: false
      })
    );
  });
};

export const onSave = (type, isThemeEditor, dispatch, cb) => {
  const saveMap = {
    chart: saveChartAction,
    layout: saveLayoutAction,
    table: saveTableAction
  };

  let save = isThemeEditor ? saveThemeAction : saveMap[type];

  dispatch(
    save({
      reload: false,
      hideNotification: true,
      callback: (data) => {
        if (type === 'layout' || type === 'table') {
          const typeId = type === 'layout' ? 'story_id' : 'table_id';
          window.history.pushState(null, null, `/${type}/edit/${data[typeId]}`);
        }
        if (cb) {
          // eslint-disable-next-line callback-return
          cb();
        }
      }
    })
  );
};

export const getStatus = (lastPublishTime, lastSaved) => {
  if (lastPublishTime) {
    const lastPublish = new Date(lastPublishTime);
    return <span title={lastPublish.toUTCString()}>Last published: {formatDistanceToNow(lastPublish)} ago</span>;
  }

  if (lastSaved) {
    const lastSavedTime = new Date(lastSaved);
    return <span title={lastSavedTime.toUTCString()}>Last saved: {formatDistanceToNow(lastSavedTime)} ago</span>;
  }

  return '';
};
